import { useCallback, useEffect, useState } from 'react';

interface UseResizeObserverOptions {
  /** Transform the raw measurements (e.g. `Math.floor`). Defaults to `Math.round`. */
  round?: (value: number) => number;
}

interface UseResizeObserverResult<T extends HTMLElement> {
  /** Callback ref to attach to the observed element. */
  ref: (node: T | null) => void;
  width?: number;
  height?: number;
}

/**
 * @description Observes an element's content box and reports its width/height.
 * Native `ResizeObserver` replacement for the unmaintained `use-resize-observer`
 * package (which capped its react peer at <19). API-compatible with the subset we use:
 * `const { ref, width, height } = useResizeObserver<HTMLDivElement>({ round: Math.floor })`.
 */
export const useResizeObserver = <T extends HTMLElement>(
  options: UseResizeObserverOptions = {},
): UseResizeObserverResult<T> => {
  const { round = Math.round } = options;
  const [element, setElement] = useState<T | null>(null);
  const [size, setSize] = useState<{ width?: number; height?: number }>({ width: undefined, height: undefined });

  const ref = useCallback((node: T | null) => setElement(node), []);

  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const width = round(entry.contentRect.width);
      const height = round(entry.contentRect.height);
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [element, round]);

  return { ref, width: size.width, height: size.height };
};
